
import { Options, Vue } from "vue-class-component";
import CreateBookingClient from "../../create-booking-client.vue";
import { ShipmentBookingController } from "@/app/ui/controllers/ShipmentBookingController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  AdditionalDocumentBookingV4,
  AddressTypeV4,
  BookingTabV4,
  CommodityBookingV4,
  CommodityCiplBookingV4,
  CreateBookingV4,
  DocInternationalBookingV4,
  ErrorPiecesBookingV4,
  PageMetaBookingV4,
  PaymentMethodBookingV4,
  PaymentMethodV4,
  PieceBookingV4,
  ProtectionPackingBookingV4,
  ReceiverBookingV4,
  SenderBookingV4,
  SupportingDocBookingV4
} from "@/domain/entities/BookingV4";
import { ShipmentBookingDetail } from "@/domain/entities/ShipmentBooking";
import { OptionsClass } from "@/domain/entities/MainApp";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { DistrictData } from "@/domain/entities/Location";
import { PartnerController } from "@/app/ui/controllers/PartnerController";
import { CommodityController } from "@/app/ui/controllers/CommodityController";
import { Commodity } from "@/domain/entities/Commodity";
import { AccountController } from "@/app/ui/controllers/AccountController";
import {
  formatInputMoney,
  formatInputMoneyWithDecimal,
  formatInputNumberOnly,
  formatInputByCountry
} from "@/app/infrastructures/misc/Utils";

@Options({
  components: {
    CreateBookingClient
  }
})
class DuplicateClientV4 extends Vue {
  declare $refs: {
    createBooking: any;
  };

  mounted() {
    this.getDetailSTT();
  }

  get accountIsForeign() {
    return AccountController.accountData.accountIsForeign;
  }

  get accountCountryCode(): string {
    return AccountController.accountData.countryCode;
  }

  async getDetailSTT() {
    try {
      MainAppController.showLoading();
      this.$refs.createBooking.isPrefillingPaymentMethod = true;
      const res = await ShipmentBookingController.getEditBookingDetail({
        id: this.$route.query.id as any,
        sttNo: this.$route.params.id as any,
        isAuth: true,
        pageType: "duplicate_stt"
      });
      if (res instanceof ShipmentBookingDetail) {
        const sender = await this.mapSenderForm(res);
        const receiver = await this.mapReceiverForm(res);
        const commodity = await this.mapCommodity(res);
        const pieces = await this.mapPieces(res);
        const protectionPacking = await this.mapProtectionPacking(res);
        const paymentMethod = await this.mapPaymentMethod(res);
        const supportingDoc = await this.mapSupportingDoc(res);
        const commodityCipl = await this.mapCommodityCipl(res);

        const originCountryCode = (
          sender.originCountryCode ||
          sender.clientCountryCode ||
          res.sttBookedByCountry ||
          ""
        ).toUpperCase();
        const destinationCountryCode = (
          receiver.destinationCountryCode || ""
        ).toUpperCase();
        const hasCountryRoute = Boolean(
          originCountryCode && destinationCountryCode
        );
        const isInternational = hasCountryRoute
          ? originCountryCode !== destinationCountryCode
          : Boolean(res.sttCipl.length) ||
            res.sttSenderPhoneFlag !== res.sttRecipientPhoneFlag;
        const activeTab = isInternational
          ? BookingTabV4.INTERNATIONAL
          : BookingTabV4.DOMESTIC;

        Object.assign(
          this.$refs.createBooking.bookingFormV4,
          new CreateBookingV4({
            ...this.$refs.createBooking.bookingFormV4,
            pageMeta: new PageMetaBookingV4({
              ...this.$refs.createBooking.pageMeta,
              activeTab
            }),
            sender,
            receiver,
            commodity,
            pieces,
            protectionPacking,
            paymentMethod,
            supportingDoc,
            commodityCipl
          })
        );

        await this.$nextTick();
        this.$refs.createBooking.onCommodityUpdate(commodity);
        this.setProductType(res);
        await this.$nextTick();
        this.$refs.createBooking.bookingFormV4.protectionPacking.woodpacking =
          res.sttIsWoodPacking;
        await this.$nextTick();
        this.$refs.createBooking.bookingFormV4.sender.id =
          res.sttSenderCustomerId;
        await this.$nextTick();
        this.$refs.createBooking.onSetInsurance();
        await this.$nextTick();
        await this.$nextTick();
        const isInsuranceFree = Boolean(res.sttInsuranceName.match(/free/gi));
        this.$refs.createBooking.bookingFormV4.protectionPacking = new ProtectionPackingBookingV4(
          {
            ...this.$refs.createBooking.bookingFormV4.protectionPacking,
            insuranceType: isInsuranceFree ? "free" : "basic",
            protectionChecked: !isInsuranceFree
          }
        );
        await this.$nextTick();
        this.$refs.createBooking.getInsuranceAvailability();
        this.$refs.createBooking.setDuplicateCommodityCipl();
        await this.$refs.createBooking.getCodConfig();
        await this.$nextTick();
      }
    } finally {
      this.$refs.createBooking.isPrefillingPaymentMethod = false;
      this.$refs.createBooking.checkPaymentMethod();
      MainAppController.closeLoading();
    }
  }

  async mapSenderForm(res: ShipmentBookingDetail) {
    if (res.sttClient.clientId && !AccountController.accountData.isAllClient)
      await this.$refs.createBooking.fetchAndApplyClientDetail({
        clientId: res.sttClient.clientId
      });
    let originCity = await LocationController.getDetailDistrictByDistrictCode({
      districtCode: res.sttOriginDistrictId
    });

    if (Array.isArray(originCity)) {
      originCity = originCity[0];
    }

    await this.$nextTick();
    return new SenderBookingV4({
      ...this.$refs.createBooking.bookingFormV4.sender,
      name: res.sttSenderName,
      address: res.sttSenderAddress,
      phone: res.sttSenderPhone,
      phoneCode: res.sttSenderPhoneCode,
      phoneFlag: res.sttSenderPhoneFlag,
      id: res.sttSenderCustomerId,
      originCountryCode: (originCity as DistrictData).countryCode
    });
  }

  async mapReceiverForm(res: ShipmentBookingDetail) {
    let destinationCity = await LocationController.getDetailDistrictByDistrictCode(
      {
        districtCode: res.sttDestinationDistrictId
      }
    );

    if (Array.isArray(destinationCity)) {
      destinationCity = destinationCity[0];
    }

    const postalCode = new DistrictData({
      ...destinationCity,
      name: res.postalCodeDestination,
      destinationZipCode: res.postalCodeDestination
    });

    const receiver = new ReceiverBookingV4({
      address: res.sttRecipientAddress,
      addressType: new OptionsClass({
        value:
          res.sttRecipientAddressType === "home"
            ? AddressTypeV4.HOME
            : AddressTypeV4.OFFICE
      }),
      destinationCity: destinationCity as DistrictData,
      // GENESIS-23376 — Fixing ini mungkin impact ke Duplicate STT Client
      // International MY→ID. Minta tolong cek kecamatan dan postal code tujuan.
      destinationCountryCode: (
        res.sttDestinationCountryCode ||
        (destinationCity as DistrictData).countryCode
      ).toUpperCase(),
      postalCode: postalCode,
      name: res.sttRecipientName,
      phoneFlag: res.sttRecipientPhoneFlag,
      phoneCode: res.sttRecipientPhoneCode,
      phone: res.sttRecipientPhone
    });

    return receiver;
  }

  async mapCommodity(res: ShipmentBookingDetail) {
    const item = (await CommodityController.getCommodityDetail({
      id: res.sttCommodityId
    })) as Commodity;
    return new CommodityBookingV4({
      id: item.commodity_id,
      name: item.commodity_name,
      nameEn: item.commodity_name_en,
      code: item.commodity_code,
      isDangerousGoods: item.isDangerousGoods,
      isQuarantine: item.isQuarantine,
      status: item.commodity_status
    });
  }

  async mapPieces(res: ShipmentBookingDetail) {
    return res.SttPieces.map((piece) => {
      return new PieceBookingV4({
        error: new ErrorPiecesBookingV4(),
        grossWeight: piece.grossWeight,
        volumeWeight: piece.volumeWeight,
        height: piece.dimension.height,
        length: piece.dimension.length,
        width: piece.dimension.width
      });
    });
  }

  async mapProtectionPacking(res: ShipmentBookingDetail) {
    const isInsuranceFree = Boolean(res.sttInsuranceName.match(/free/gi));
    return new ProtectionPackingBookingV4({
      ...this.$refs.createBooking.bookingFormV4.protectionPacking,
      woodpacking: res.sttIsWoodPacking,
      insuranceType: isInsuranceFree ? "free" : "basic",
      itemPrice: res.sttGoodsEstimatePriceForDetail,
      protectionDisabled: false,
      protectionChecked: !isInsuranceFree
    });
  }

  async mapPaymentMethod(res: ShipmentBookingDetail) {
    let paymentMethod: typeof PaymentMethodV4[keyof typeof PaymentMethodV4] =
      PaymentMethodV4.CASH;
    let codAmount = 0;

    if (res.sttIsCod && !res.sttIsDfod) paymentMethod = PaymentMethodV4.COD;
    if (res.sttIsCod && res.sttIsDfod) paymentMethod = PaymentMethodV4.DFOD;

    if (
      paymentMethod === PaymentMethodV4.COD &&
      res.sttClient.clientCodConfigAmount === "goods_price"
    ) {
      codAmount = res.sttCodAmount;
    }

    return new PaymentMethodBookingV4({
      codAmount: codAmount,
      method: paymentMethod
    });
  }

  async mapSupportingDoc(res: ShipmentBookingDetail) {
    const attachFiles = res.sttAttachFiles.length
      ? res.sttAttachFiles
      : res.sttFtzAttachFiles;
    const additionalDocuments: AdditionalDocumentBookingV4[] = attachFiles
      .slice(2)
      .map((url, index) => {
        const extractedFileName =
          url.split("/").pop() || `Dokumen Pendukung ${index + 1}`;

        return new AdditionalDocumentBookingV4({
          id: String(index + 1),
          url: url,
          fileName: extractedFileName,
          fileSize: 0
        });
      });
    const docInternational = new DocInternationalBookingV4({
      email: res.sttRecipientEmail,
      dutyTaxTerms: res.sttDutyTaxTerms as any,
      imageIdUrl: res.sttKtpImage,
      imageTaxUrl: res.sttTaxImage,
      imageAfterPackingUrl: res.sttAttachFiles.length
        ? res.sttAttachFiles[0]
        : "",
      imageBeforePackingUrl:
        res.sttAttachFiles.length >= 2 ? res.sttAttachFiles[1] : "",
      idNumber: res.sttIdentityNumber,
      taxNumber: res.sttInterTaxNumber
    });
    const ftzForm = new DocInternationalBookingV4({
      email: res.sttFtzRecipientEmail,
      idNumber: res.sttFtzIdentityNumber,
      taxNumber: res.sttTaxNumber,
      imageIdUrl: res.sttFtzKtpImage,
      imageTaxUrl: res.sttFtzTaxImage,
      imageAfterPackingUrl: res.sttFtzAttachFiles.length
        ? res.sttFtzAttachFiles[0]
        : "",
      imageBeforePackingUrl:
        res.sttFtzAttachFiles.length >= 2 ? res.sttFtzAttachFiles[1] : ""
    });

    return new SupportingDocBookingV4({
      additionalDocuments,
      docInternational,
      ftzForm
    });
  }

  formatGoodsPrice(value: string) {
    return formatInputByCountry(value, {
      type: "money",
      country: this.accountCountryCode.toUpperCase()
    });
  }

  async mapCommodityCipl(res: ShipmentBookingDetail) {
    let cipls = res.sttCipl;
    if (res.sttFtzCipl.length) cipls = res.sttFtzCipl;
    return cipls.map((cipl: any, index: number) => {
      return new CommodityCiplBookingV4({
        id: index + 1,
        name: this.accountIsForeign ? cipl.item_detail_en : cipl.item_detail,
        hsCode: cipl.commodity_hs_code,
        quantity: formatInputNumberOnly(String(cipl.quantity)),
        goodsPrice: this.formatGoodsPrice(cipl.item_price)
      });
    });
  }

  setProductType(res: ShipmentBookingDetail) {
    this.$refs.createBooking.onProductTypeSelect(res.sttProductTypeName);
  }
}

export default DuplicateClientV4;
